<template>
  <ContentSection
    :id="item.fields.id ?? item.sys.id"
    :color-mode="productListTheme ?? 'light'"
    v-bind="
      ContentfulLivePreview.getProps({
        entryId: item.sys.id,
        fieldId: 'type'
      })
    "
  >
    <MappedComponents
      v-if="props.item.fields.customContent"
      :items="props.item.fields.customContent"
    />

    <LensSelectorInModal
      v-if="tool"
      :tool="tool"
      :ga-event-category="'list'"
      :ls-link-type="'list'"
      @focal-length="(minValue, maxValue) => setFocalLength(minValue, maxValue)"
    />

    <client-only>
      <ShopProductsList
        :parent-section-id="item.fields.id ?? item.sys.id"
        :locale="locale"
        :store="$shopStore"
        :static-filters="staticFilters"
        :initial-filters="initialFilters"
        :view="productListView"
        :type="productListType"
        :discontinued-specs="discontinuedSpecifications"
        :show-in-a-link="showLinkToInterfaceAdvisorInFilter"
        :lens-selector-in-modal="tool"
        :is-full-size-image="isFullSizeImage"
        :item="item"
      >
        <template #initialLoadingError>
          <TextBlock :cols="10" :cols-centered="true" :content-centered="false">
            <LibRichTextRenderer
              v-if="initialLoadingErrorRichtext"
              :rich-text="initialLoadingErrorRichtext"
            />
          </TextBlock>
        </template>
      </ShopProductsList>
      <template #placeholder>
        <ProductsListSectionSkeleton
          :view="productListView"
          :type="productListType"
        />
      </template>
    </client-only>
  </ContentSection>
</template>

<script setup lang="ts">
import { ContentSection, TextBlock } from '@hypercodestudio/basler-components';
import { provide } from 'vue';
import { v } from 'h3-valibot';
import { createFiltersFromQuery } from '~/utils/createFiltersFromQuery';
import type { IProductList } from '~/lib/ContentfulService';
import LensSelectorInModal from '~/components/lib/Tools/LensSelectorInModal.vue';
import { parseQuery } from '#vue-router';
import { LAST_TIME_BUY, LAST_TIME_SERVICE } from '~/utils/shop/constants';
import type { ColorMode } from '@hypercodestudio/basler-components/dist/components/content-modules/sections/ContentSection.vue';
import ProductsListSectionSkeleton from '~/components/shop/skeletons/ProductsListSectionSkeleton.vue';
import {
  createProductFilterStore,
  PRODUCT_FILTER_STORE_INJECT_KEY
} from '~/stores/productFilterStore/productFilterStore';
import { ContentfulLivePreview } from '@contentful/live-preview';

interface Props {
  item: IProductList;
}

const props = defineProps<Props>();

const { $shopStore, $dictionary } = useNuxtApp();
const locale = useLocale();

const initialLoadingErrorRichtext = computed(
  () => $dictionary.value['productsList.initialLoadingError.message']
);

const filtersSchema = v.record(v.string(), v.array(v.string()));

const tool = computed(() =>
  props.item?.fields?.guidanceTool?.fields?.type == 'tool'
    ? props.item.fields.guidanceTool
    : undefined
);

const staticFilters = computed<Record<string, string[]>>(() => {
  return parseFiltersWithFallback(props.item.fields?.staticFilters, {});
});

const initialFilters = computed<Record<string, string[]>>(() =>
  parseFiltersWithFallback(
    createFiltersFromQuery(
      parseQuery(props.item.fields?.filterQueryParameters ?? '')
    ),
    {}
  )
);

function setFocalLength(minValue: string, maxValue: string) {
  store.handleFormValueChange('min_focallength_range', [minValue, maxValue]);
}

function parseFiltersWithFallback(
  filters: unknown,
  fallback: Record<string, string[]>
) {
  if (!filters) {
    return fallback;
  }

  const parsedFilters = v.safeParse(filtersSchema, filters);
  if (parsedFilters.success) {
    return parsedFilters.output;
  }

  return fallback;
}
const showLinkToInterfaceAdvisorInFilter = computed(() => {
  return props.item.fields.showLinkToInterfaceAdvisorInFilter ?? false;
});
const isFullSizeImage = computed(() => {
  return props.item.fields.fullSizeImage ?? false;
});

const productListTheme = computed(
  // TODO: theme has different values in contentful that are allowed in
  //    the hypercode component
  () => (props.item.fields.theme as ColorMode) ?? 'light'
);
const productListType = computed(
  () => props.item.fields.type ?? 'Specifications'
);
const productListView = computed(() => {
  return props.item.fields.view ?? 'grid';
});
const discontinuedSpecifications = computed(() => {
  return (
    props.item.fields.discontinuedSpecifications ?? [
      LAST_TIME_BUY,
      LAST_TIME_SERVICE
    ]
  );
});

const store = createProductFilterStore(props.item.sys.id);

provide(PRODUCT_FILTER_STORE_INJECT_KEY, store);
</script>

<style>
.tab-panel__content .content-section {
  padding-block: 0px;
}
.tab-panel__content .content-section__inner {
  padding-inline: 0px;
}
</style>
